import { colors } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { text1 } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import {
  getComplaints,
  getUnknownError,
} from 'integrations/common/streams/transferState/utils';
import {
  ErrorPanel,
  WarningPanel,
} from 'integrations/common/components/Complaints/Complaints';
import { IntegrationDate } from 'integrations/common/components/integrationDate/IntegrationDate';
import { ExpandedRowMapping } from 'integrations/common/components/expandedRowMapping/ExpandedRowMapping';
import {
  getScheduleDirectionId,
  isITPediaSchedule,
  isJobOptionsWithWorkspace,
} from 'integrations/common/streams/schedules/utils';

import { IntegrationWorkspace } from '@ardoq/api-types/integrations';
import { isJobWithTabularMapping } from 'integrations/common/utils/scheduleApi';
import { FlexBox } from '@ardoq/layout';
import { IconName } from '@ardoq/icons';
import { ArdoqId, IntegrationSchedule } from '@ardoq/api-types';
import { ITPediaDetails } from './ITPediaDetails';
import { type I18nLocale, t, type WithI18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { connect } from '@ardoq/rxbeach';
import { map } from 'rxjs';
import {
  persistentWorkspaces$ as workspaces$,
  WorkspacesState,
} from 'streams/workspaces/workspaces$';

const getDirectionLabel = (
  i18nLocale: I18nLocale,
  type: IntegrationSchedule['jobOptions']['type']
): string => {
  if (getScheduleDirectionId(type) === 'export') {
    return t(
      i18nLocale,
      'integrations:expandedSchedule.directionExport',
      'export'
    );
  }
  return t(
    i18nLocale,
    'integrations:expandedSchedule.directionImport',
    'import'
  );
};

const ScheduleText = styled.div`
  ${text1}
`;

type ExpandedScheduleProps = {
  schedule: IntegrationSchedule;
  connectionName: string | null;
  onWorkspaceClick: ((workspace: IntegrationWorkspace) => void) | null;
  i18nLocale: I18nLocale;
  locale: Locale;
};

type ExpandedScheduleViewProps = ExpandedScheduleProps & {
  loadedWorkspaceIds: ReadonlySet<ArdoqId>;
};

const toLoadedWorkspaceIds = (
  workspaces: WorkspacesState
): { loadedWorkspaceIds: ReadonlySet<ArdoqId> } => ({
  loadedWorkspaceIds: new Set(Object.keys(workspaces.byId)),
});

function ExpandedScheduleBase({
  schedule,
  connectionName,
  onWorkspaceClick,
  i18nLocale,
  locale,
  loadedWorkspaceIds,
}: ExpandedScheduleViewProps) {
  const { jobOptions, meta } = schedule;
  const complaints = getComplaints(meta.lastResult?.summary?.validation);

  const { status, message } = meta.lastResult || {};

  const errors = complaints.errors.length
    ? complaints.errors
    : status === 'error'
      ? getUnknownError(message)
      : [];

  const directionLabel = getDirectionLabel(i18nLocale, jobOptions.type);

  return (
    <FlexBox
      flexDirection="column"
      gap="medium"
      backgroundColor="bgSubtle"
      padding="large"
    >
      <FlexBox justify="space-between" gap="medium">
        <ScheduleText>
          {t(
            i18nLocale,
            'integrations:expandedSchedule.connectionLabel',
            'Connection:'
          )}{' '}
          {connectionName || <MissingConnection i18nLocale={i18nLocale} />}
        </ScheduleText>
      </FlexBox>
      {isJobOptionsWithWorkspace(jobOptions) && (
        <FlexBox justify="space-between">
          <ScheduleText>
            {t(
              i18nLocale,
              'integrations:expandedSchedule.workspaceLabel',
              'Workspace:'
            )}{' '}
            {jobOptions.workspace.id &&
            loadedWorkspaceIds.has(jobOptions.workspace.id) &&
            onWorkspaceClick ? (
              <Link
                iconName={IconName.WORKSPACE}
                type="primary"
                typography="text1"
                onClick={() => onWorkspaceClick(jobOptions.workspace)}
              >
                {jobOptions.workspace.name}
              </Link>
            ) : (
              jobOptions.workspace.name
            )}
          </ScheduleText>
        </FlexBox>
      )}
      <FlexBox justify="space-between" gap="medium">
        <ScheduleText>
          {t(
            i18nLocale,
            'integrations:expandedSchedule.firstScheduled',
            { direction: directionLabel },
            'First scheduled {{direction}}:'
          )}{' '}
          <IntegrationDate
            date={meta.firstCompleted}
            locale={locale}
            i18nLocale={i18nLocale}
          />
        </ScheduleText>
        <ScheduleText>
          {t(
            i18nLocale,
            'integrations:expandedSchedule.nextScheduled',
            { direction: directionLabel },
            'Next scheduled {{direction}}:'
          )}{' '}
          <IntegrationDate
            date={meta.nextStart}
            locale={locale}
            i18nLocale={i18nLocale}
          />
        </ScheduleText>
      </FlexBox>
      <WarningPanel
        warnings={complaints.warnings}
        isCollapsible={false}
        i18nLocale={i18nLocale}
      />
      <ErrorPanel
        errors={errors}
        isCollapsible={false}
        i18nLocale={i18nLocale}
      />
      {isJobWithTabularMapping(jobOptions) && (
        <ExpandedRowMapping
          dataSource={jobOptions.tabularMapping.tables.map(table => ({
            name: table.name,
            id: table.id,
            rowRepresentation: table.rowRepresentation,
            workspace: table.rootWorkspace,
            isWorkspaceAvailable: table.rootWorkspace.id
              ? loadedWorkspaceIds.has(table.rootWorkspace.id)
              : false,
          }))}
          onWorkspaceClick={onWorkspaceClick}
          i18nLocale={i18nLocale}
        />
      )}
      {isITPediaSchedule(schedule) && (
        <ITPediaDetails
          schedule={schedule}
          onWorkspaceClick={onWorkspaceClick}
          i18nLocale={i18nLocale}
        />
      )}
    </FlexBox>
  );
}

export const ExpandedSchedule = connect(
  ExpandedScheduleBase,
  workspaces$.pipe(map(toLoadedWorkspaceIds))
);

const GreyText = styled.span`
  color: ${colors.grey500};
`;

const MissingConnection = ({ i18nLocale }: WithI18nLocale) => (
  <GreyText>
    {t(i18nLocale, 'integrations:expandedSchedule.missing', 'Missing')}
  </GreyText>
);
